/**
 * Short, not long. `29. September – 1. Oktober` is 26 characters; at 320px this label has about
 * 104px to live in once `Heute`, the arrows and the refresh button have taken theirs, and a
 * truncated month is worse than an abbreviated one. de-DE gives `Mai`, `Sept.`, `Dez.`.
 */
const MONTH_SHORT = new Intl.DateTimeFormat('de-DE', { month: 'short' })

/**
 * The visible window as a range, e.g. `27.–29. Mai`.
 *
 * The header used to name only the month, which is the one fact the three day columns underneath
 * already spell out — every column carries its own day number and short month. A range says the
 * thing they cannot: how far the window reaches, which matters here because the window skips
 * weekends and so is not always three consecutive dates.
 *
 * Three shapes, cheapest first:
 *
 *   same month   `27.–29. Mai`
 *   month change `29. Mai – 1. Juni`
 *   other year   `30. Dez. 2026 – 1. Jan. 2027`   (year only when it is not the current one)
 *
 * Never emits a bare day number as its whole output — `WorkerCalendarWidget`'s tests match day
 * numbers by exact string against the columns, and a header reading `"27"` would collide.
 */
export function formatDayWindow(days: Date[], today: Date = new Date()): string {
  if (days.length === 0) return ''

  const first = days[0]
  const last = days[days.length - 1]

  const spansYear = first.getFullYear() !== last.getFullYear()
  const showYear = spansYear || first.getFullYear() !== today.getFullYear()

  const month = (d: Date) => MONTH_SHORT.format(d)
  const year = (d: Date) => String(d.getFullYear())

  if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()) {
    const head = `${first.getDate()}.–${last.getDate()}. ${month(last)}`
    return showYear ? `${head} ${year(last)}` : head
  }

  const left = showYear
    ? `${first.getDate()}. ${month(first)} ${year(first)}`
    : `${first.getDate()}. ${month(first)}`
  const right = showYear
    ? `${last.getDate()}. ${month(last)} ${year(last)}`
    : `${last.getDate()}. ${month(last)}`

  return `${left} – ${right}`
}
